/**
 * LeaderboardService - Handles API calls and WebSocket connection for real-time leaderboard
 */

export interface LeaderboardEntry {
  rank: number;
  id: string;
  name: string;
  score: number;
  timestamp: number;
  date: string;
}

export interface ScoreSubmission {
  playerId?: string;
  playerName: string;
  score: number;
  sessionId?: string;
  gameMetadata?: GameMetadata;
}

export interface GameMetadata {
  gameDuration: number;      // Game duration in milliseconds
  aliensDestroyed: number;   // Total aliens destroyed
  wavesCompleted?: number;   // Number of waves completed
  powerUpsCollected?: number; // Number of power-ups collected
}

export interface LeaderboardUpdateEvent {
  type: 'leaderboard_update';
  data: LeaderboardEntry[];
  newScore?: LeaderboardEntry & { rank: number | null };
}

type LeaderboardEventHandler = (event: LeaderboardUpdateEvent) => void;

export class LeaderboardService {
  private static instance: LeaderboardService;
  private baseUrl: string;
  private wsUrl: string;
  private ws: WebSocket | null = null;
  private eventHandlers: Set<LeaderboardEventHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private playerId: string;
  private isConnecting = false;
  private currentSessionId: string | null = null;

  private constructor() {
    // Determine API URL based on environment
    const isDev = import.meta.env.DEV;
    this.baseUrl = isDev
      ? 'http://localhost:3002/api'
      : `${window.location.origin}/api`;
    this.wsUrl = isDev
      ? 'ws://localhost:3002/ws'
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;

    // Get or generate player ID
    this.playerId = this.getOrCreatePlayerId();
  }

  static getInstance(): LeaderboardService {
    if (!LeaderboardService.instance) {
      LeaderboardService.instance = new LeaderboardService();
    }
    return LeaderboardService.instance;
  }

  private getOrCreatePlayerId(): string {
    const storageKey = 'spaceInvaders2030_playerId';
    let playerId = localStorage.getItem(storageKey);

    if (!playerId) {
      playerId = crypto.randomUUID?.() || this.generateUUID();
      localStorage.setItem(storageKey, playerId);
    }

    return playerId;
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getPlayerId(): string {
    return this.playerId;
  }

  /**
   * Start a new game session for score validation
   */
  async startGameSession(): Promise<string | null> {
    try {
      const response = await fetch(`${this.baseUrl}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId: this.playerId,
        }),
      });

      if (!response.ok) {
        console.error('Failed to create game session:', response.status);
        return null;
      }

      const result = await response.json();

      if (result.success && result.data?.sessionId) {
        this.currentSessionId = result.data.sessionId;
        return this.currentSessionId;
      }

      return null;
    } catch (err) {
      console.error('Failed to create game session:', err);
      return null;
    }
  }

  /**
   * Get the current game session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Clear the current session (after score submission)
   */
  clearSession(): void {
    this.currentSessionId = null;
  }

  /**
   * Connect to WebSocket for real-time updates
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        console.log('Leaderboard WebSocket connected');
        this.reconnectAttempts = 0;
        this.isConnecting = false;
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as LeaderboardUpdateEvent;
          this.notifyHandlers(message);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      this.ws.onclose = () => {
        console.log('Leaderboard WebSocket disconnected');
        this.isConnecting = false;
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('Leaderboard WebSocket error:', error);
        this.isConnecting = false;
      };
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
      this.isConnecting = false;
      this.attemptReconnect();
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Subscribe to leaderboard updates
   */
  subscribe(handler: LeaderboardEventHandler): () => void {
    this.eventHandlers.add(handler);

    // Connect if not already connected
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.connect();
    }

    // Return unsubscribe function
    return () => {
      this.eventHandlers.delete(handler);
    };
  }

  private notifyHandlers(event: LeaderboardUpdateEvent): void {
    for (const handler of this.eventHandlers) {
      try {
        handler(event);
      } catch (err) {
        console.error('Error in leaderboard event handler:', err);
      }
    }
  }

  /**
   * Fetch current leaderboard
   */
  async getLeaderboard(limit = 10, offset = 0): Promise<LeaderboardEntry[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/leaderboard?limit=${limit}&offset=${offset}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch leaderboard');
      }

      return result.data;
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
      return [];
    }
  }

  /**
   * Submit a score to the leaderboard
   */
  async submitScore(
    score: number,
    playerName = 'Anonymous',
    gameMetadata?: GameMetadata
  ): Promise<{ success: boolean; rank?: number; entry?: LeaderboardEntry; error?: string }> {
    try {
      const payload: ScoreSubmission = {
        playerId: this.playerId,
        playerName: playerName.slice(0, 20),
        score,
      };

      // Include session ID if available
      if (this.currentSessionId) {
        payload.sessionId = this.currentSessionId;
      }

      // Include game metadata for validation
      if (gameMetadata) {
        payload.gameMetadata = gameMetadata;
      }

      const response = await fetch(`${this.baseUrl}/scores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Clear session after submission attempt (whether successful or not)
      this.clearSession();

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.error || `HTTP error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit score');
      }

      return {
        success: true,
        rank: result.data.rank,
        entry: result.data,
      };
    } catch (err) {
      console.error('Failed to submit score:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to submit score',
      };
    }
  }

  /**
   * Get player's current rank and nearby scores
   */
  async getPlayerRank(range = 2): Promise<{
    player: LeaderboardEntry | null;
    neighbors: LeaderboardEntry[];
  }> {
    try {
      const response = await fetch(
        `${this.baseUrl}/scores/${this.playerId}?range=${range}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          return { player: null, neighbors: [] };
        }
        throw new Error(`HTTP error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch player rank');
      }

      return result.data;
    } catch (err) {
      console.error('Failed to fetch player rank:', err);
      return { player: null, neighbors: [] };
    }
  }

  /**
   * Check if the service is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
export const leaderboardService = LeaderboardService.getInstance();
